import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchGoalsData } from '../data/goalsApi.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import { DEFAULT_META } from '../data/metaApi.js'

/**
 * Top-level hook for the full goals data array.
 *
 * Archive-aware: when the archiveViewStore is active, this returns the
 * `goals` slice of the snapshot for that week instead of live data.
 * Every consumer downstream (pillar tabs, goal cards, sub-goal rows)
 * gets the frozen structure automatically. If the snapshot is from
 * before we captured goals data (pre-2026-04-23), the hook returns
 * `null` so UI can render a "not available" state.
 *
 * Live path (default):
 *   cache key ['goals'], long staleTime, no refetchOnWindowFocus. KV's
 *   eventual consistency means a refetch soon after a save can return
 *   stale data; Ably handles real-time cross-client sync.
 */
export function useGoalsData() {
  const queryClient = useQueryClient()
  const { active, weekKey } = useArchiveViewStore((s) => ({
    active: s.active,
    weekKey: s.weekKey,
  }))

  const live = useQuery({
    queryKey: ['goals'],
    queryFn: fetchGoalsData,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    // Don't refetch while viewing an archive — we don't need fresh live
    // data and the request is wasted.
    enabled: !active,
  })

  if (!active) return live

  // Read archived goals from the cached meta.weeklyHistory[weekKey].
  // We don't need a separate fetch — the full meta blob is already
  // cached by useMetaData and contains weeklyHistory.
  const meta = queryClient.getQueryData(['meta']) || DEFAULT_META
  const snapshot = meta?.weeklyHistory?.[weekKey] || null
  const archivedGoals = snapshot?.goals || null

  return {
    data: archivedGoals,
    isLoading: false,
    isFetching: false,
    error: archivedGoals ? null : new Error('This week has no goals snapshot'),
    isSuccess: !!archivedGoals,
  }
}
